import {
    View, colors, Grid, Page, GridColumn
} from '@fairygodboss/ui';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import React, { useRef, memo } from 'react';

import {
    PAGE_VIEW_SECTIONS,
    PAGE_VIEW_TYPES,
    SnowplowPageViewTracker,
} from '../utils/Analytics/SnowPlow/SnowPlowPageViewtracker';
import { IMAGE_CDN_URL } from '../utils/imageCDN';

import {
    AboveTheFoldLink,
    ContentInfo,
    ContentTitle,
    MainArticleBody,
} from './components';
import { DefaultAuthor, LayoutProps } from './interfaces';

const BottomConvincer = dynamic(
    () => import('../components/SharedLayout/Convincers/BottomConvincer').then(
        (mod) => mod.BottomConvincer
    ),
    {
        loading: () => <></>,
        ssr: false,
    }
);
const PageMetrics = dynamic(
    () => import('../utils/Analytics/PageMetrics').then((mod) => mod.default),
    {
        loading: () => <></>,
        ssr: false,
    }
);

const GoogleOneTap = dynamic(
    () => import('../components/Login/GoogleOneTap/GoogleOneTap').then(
        (mod) => mod.default
    ),
    {
        loading: () => <></>,
        ssr: false,
    }
);

const RecentContentContainer = dynamic(
    () => import('./components/RecentContentContainer').then(
        (mod) => mod.RecentContentContainer
    ),
    {
        loading: () => <></>,
        ssr: false,
    }
);
const ModalHandler = dynamic(
    () => import('./components/ModalHandler').then((mod) => mod.ModalHandler),
    {
        loading: () => <></>,
        ssr: false,
    }
);

const Footer = dynamic(
    () => import('../components/FooterV2').then((mod) => mod.Footer),
    {
        loading: () => <></>,
        ssr: false,
    }
);

const CommentContainer = dynamic(
    () => import('../components/SharedLayout/Comments/CommentContainer').then(
        (mod) => mod.CommentContainer
    ),
    {
        loading: () => <></>,
        ssr: false,
    }
);

const SocialCTA = dynamic(
    () => import('./components/SocialCTA').then((mod) => mod.SocialCTA),
    {
        loading: () => <></>,
        ssr: false,
    }
);

export const Layout = memo<LayoutProps>(
    ({
        article,
        hash,
        type,
        parsedContentBody,

        pageName,
    }) => {
        const backgroundHeight = 800;

        const topConvincerRef = useRef<HTMLBodyElement>();
        const articlePictureRef = useRef<HTMLBodyElement>();

        const contentTitle = article.title;
        if (!contentTitle || !parsedContentBody) {
            throw new Error('Article not found ');
        }
        const { paidCompany } = article;
        const { author } = article;
        const contentInfoProps = {
            contentImage: article.contentImage,
            contentTitle,
            author: author || DefaultAuthor,
            ref: articlePictureRef,
            contentCreatedDate: article.createdAt,
            contentModifiedDate: article.contentModifiedDate,
            paidCompany,
            isSponsor: article.isSponsor,
            showSponsoredBy: article.showSponsoredBy,
            topConvincerRef,
            description: article.description,
            hash,
            type,
            backgroundHeight,
        };
        const renderArticleStructuredData = () => {
            const {
                contentImage, publishedAt, modifiedAt, metaDescription
            } = article;
            const structuredDataAuthor = author || DefaultAuthor;
            const contentDate = new Date(publishedAt).toISOString();
            const parsedContentModifiedDate = new Date(
                modifiedAt
            ).toISOString();
            const urlType = 'career-topics';
            let nameToUse = structuredDataAuthor.authorName
                || (structuredDataAuthor.active
                    ? structuredDataAuthor.username
                    : 'Fairygodboss');
            nameToUse = nameToUse || 'Fairygodboss';
            const isOrganization = nameToUse === 'Fairygodboss';
            const authorUrl = structuredDataAuthor.authorHash
                ? `/users/profile/${structuredDataAuthor.authorHash}/${nameToUse}`
                : null;
            const articleJson = {
                '@context': 'http://schema.org',
                '@type': 'Article',
                url: `https://fairygodboss.com/${urlType}/${hash}`,
                headline: contentTitle?.replace(/"/g, '\''),
                image: {
                    '@type': 'ImageObject',
                    url: contentImage
                        ? `https://d207ibygpg2z1x.cloudfront.net/image/upload${contentImage?.src}`
                        : '',
                    width: '669px',
                    height: '337px',
                },
                datePublished: contentDate,
                dateModified: parsedContentModifiedDate,
                author: {
                    name: nameToUse?.replace(/"/g, '\''),
                    type: isOrganization ? 'Organization' : 'Person',
                    ...(isOrganization
                        ? { url: 'https://www.fairygodboss.com' }
                        : {}),
                    ...(!isOrganization && authorUrl
                        ? { url: `https://fairygodboss.com${authorUrl}` }
                        : {}),
                },
                publisher: {
                    '@type': 'Organization',
                    name: 'Fairygodboss',
                    url: 'https://www.fairygodboss.com',
                    logo: {
                        '@type': 'ImageObject',
                        url: 'https://d207ibygpg2z1x.cloudfront.net/image/upload/dpr_2.0,c_scale,g_center,r_max,q_auto,w_48,h_48/v1/production/tip/9/owner.jpg',
                        width: '48px',
                        height: '48px',
                    },
                    sameAs: [
                        'https://www.facebook.com/fairygodboss/',
                        'https://www.instagram.com/fairygodboss/',
                        'https://twitter.com/fairygodboss/',
                        'https://linkedin.com/company/fairygodboss',
                    ],
                },
                description: metaDescription,
                mainEntityOfPage: {
                    '@type': 'WebPage',
                    '@id': `https://fairygodboss.com/${urlType}/${hash}`,
                },
                isAccessibleForFree: true,
            };
            return (
                <script
                    type="application/ld+json"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(articleJson),
                    }}
                />
            );
        };

        const metaTile = article?.metaTitle && article.metaTitle !== ''
            ? article.metaTitle
            : 'Jobs and company reviews for women | Fairygodboss';

        const metaDescription = article?.metaDescription && article.metaDescription !== ''
            ? article.metaDescription
            : 'Fairygodboss';

        const canonicalUrl = article?.canonicalUrl ? (
            <link rel="canonical" href={article.canonicalUrl} />
        ) : (
            <link
                rel="canonical"
                href={`https://fairygodboss.com/career-topics/${hash}`}
            />
        );

        return (
            <>
                <Head>
                    <title>{metaTile}</title>
                    <meta
                        property="og:url"
                        content={`https://fairygodboss.com/career-topics/${hash}`}
                    />
                    <meta property="og:type" content="article" />
                    <meta
                        property="og:image"
                        content={`${IMAGE_CDN_URL}/dpr_1,q_auto,c_scale,f_webp,w_669${article.contentImage?.src}`}
                    />
                    <meta
                        property="og:image:alt"
                        content={article.contentImage?.alt}
                    />

                    <meta property="og:image:width" content="699" />

                    <meta property="og:image:height" content="377" />
                    <meta
                        name="twitter:title"
                        content={`${metaTile} | Fairygodboss`}
                        key="twittterTitle"
                    />
                    <meta
                        name="twitter:description"
                        content={metaDescription}
                        key="twitterDescription"
                    />
                    <meta
                        name="twitter:Image"
                        content={`${IMAGE_CDN_URL}/dpr_1,q_auto,c_scale,f_webp,w_669${article.contentImage?.src}`}
                        key="twitterImage"
                    />
                    <meta
                        name="twitter:image:height"
                        content="699"
                        key="twitterImageHeight"
                    />
                    <meta
                        name="twitter:image:width"
                        content="377"
                        key="twitterImageWidth"
                    />
                    <meta name="description" content={metaDescription} />
                    <meta property="og:title" content={metaTile} />
                    <meta property="og:description" content={metaDescription} />
                    <meta
                        name="twitter:title"
                        content="Fairygodboss"
                        key="twitterTitle"
                    />
                    <meta
                        name="twitter:image"
                        content="https://res.cloudinary.com/fairygodboss/image/upload/v1681846562/articles_upload/content/bepbejxmscgrncvwbzsb.webp"
                        key="twitterImage"
                    />
                    <meta
                        property="og:image"
                        content="https://res.cloudinary.com/fairygodboss/image/upload/v1681846562/articles_upload/content/bepbejxmscgrncvwbzsb.webp"
                    />
                    {canonicalUrl}
                </Head>
                {renderArticleStructuredData()}
                <View
                    display="flex"
                    width="100%"
                    color={colors.neutral[100]}
                    css={{
                        fontFamily: 'Open Sans',
                        background:
                            'linear-gradient(to bottom, #F6F2FE 660px, white 0%)',
                        '@media (min-width: 720px)': {
                            background:
                                'linear-gradient(to bottom, #F6F2FE 700px, white 0%)',
                        },
                        '@media (min-width:907px)': {
                            background:
                                'linear-gradient(to bottom, #F6F2FE 740px, white 0%)',
                        },
                        '@media (min-width: 1000px)': {
                            background:
                                'linear-gradient(to bottom, #F6F2FE 760px, white 0%)',
                        },
                    }}
                >
                    <GoogleOneTap
                        gclientId={
                            process.env.NEXT_PUBLIC_PASSPORT_GOOGLE_CLIENT_ID
                        }
                    />
                    <SnowplowPageViewTracker
                        pageCompanyId={paidCompany?.[0]?.id ?? null}
                        pageSection={PAGE_VIEW_SECTIONS.ADVICE}
                        pageEntityId={`${type}-${hash}`}
                        pageType={PAGE_VIEW_TYPES.CONTENT}
                    />

                    <View
                        display="flex"
                        width="100%"
                        flexDirection="column"
                        alignItems="center"
                    >
                        <Page width="100%">
                            <Grid
                                maxWidth="1176px"
                                justifyContent={{
                                    _: 'left',
                                    sm: 'center',
                                    md: 'left',
                                    lg: 'right',
                                }}
                            >
                                <GridColumn
                                    size={{
                                        xs: 12,
                                        sm: 9,
                                        md: 12,
                                        lg: 11,
                                    }}
                                    pl={{ _: '0px', md: '24px', lg: '8px' }}
                                    css={{
                                        paddingLeft: '0px',
                                        paddingRight: '0px',
                                    }}
                                >
                                    <AboveTheFoldLink hash={hash} type={type} />

                                    <ContentTitle contentTitle={contentTitle} />

                                    <ContentInfo {...contentInfoProps} />

                                    <MainArticleBody
                                        contentBody={parsedContentBody}
                                        isSponsored={article.isSponsor}
                                    />
                                    <SocialCTA hash={hash} type={type} />
                                </GridColumn>
                            </Grid>
                            <Grid
                                justifyContent={{
                                    _: 'left',
                                    sm: 'center',
                                    md: 'left',
                                    lg: 'right',
                                }}
                                style={{
                                    paddingLeft: '0px',
                                    paddingRight: '0px',
                                }}
                            >
                                <CommentContainer hash={hash} type={type} />
                            </Grid>
                            <RecentContentContainer
                                article={article}
                                hash={hash}
                            />

                            <BottomConvincer
                                jobPage={false}
                                loginTrigger="article_join_footer_button"
                                miscEventType="article_join_footer_button"
                                redirectUrl="/community/feed"
                                alwaysDisplayButton="none"
                                rightAltTag="Fairygodboss members"
                                leftAltTag="Fairygodboss VIP"
                            />
                            <Footer />
                            <PageMetrics
                                article={article}
                                pageName={pageName}
                                hash={hash}
                                type={type}
                            />
                            <ModalHandler article={article} />
                        </Page>
                    </View>
                </View>
            </>
        );
    }
);
